import type { DocumentFraudScoreDto, DocumentFraudScoreSignalDto } from 'korint-sdk';
import type { Tone } from '@folio/components/ui/tone';

type FraudSeverity = NonNullable<DocumentFraudScoreSignalDto['severity']>;

export const FRAUD_STATUS_TONE: Record<DocumentFraudScoreDto['status'], Tone> = {
  clean: 'success',
  review_needed: 'warning',
  fraud: 'destructive',
  pending: 'muted',
  unchecked: 'muted',
  error: 'muted',
};

export const FRAUD_SEVERITY_TONE: Record<FraudSeverity, Tone> = {
  critical: 'destructive',
  warning: 'warning',
  info: 'info',
};

// Literal classes only: consuming apps scan folio's source for Tailwind class names.
export const TONE_TEXT_CLASS: Record<Tone, string> = {
  primary: 'text-primary',
  muted: 'text-muted-foreground',
  info: 'text-blue-700',
  success: 'text-green-700',
  warning: 'text-amber-700',
  destructive: 'text-red-700',
};

export const FRAUD_VERDICTS = ['clean', 'review_needed', 'fraud'] as const;

export const isFraudVerdict = (status: DocumentFraudScoreDto['status']): boolean =>
  (FRAUD_VERDICTS as readonly string[]).includes(status);

/** A human verdict overrides the automated one. */
export const effectiveFraudStatus = (
  fraud: Pick<DocumentFraudScoreDto, 'status' | 'managerVerdict'>,
): DocumentFraudScoreDto['status'] => fraud.managerVerdict ?? fraud.status;

const SEVERITY_RANK: Record<FraudSeverity, number> = { critical: 0, warning: 1, info: 2 };

export const sortFraudSignals = (
  signals: DocumentFraudScoreSignalDto[],
): DocumentFraudScoreSignalDto[] =>
  [...signals].sort(
    (left, right) =>
      SEVERITY_RANK[left.severity ?? 'info'] - SEVERITY_RANK[right.severity ?? 'info'],
  );

const CATEGORY_BY_CODE_PREFIX = {
  iban: 'format',
  siren: 'format',
  siret: 'format',
  mrz: 'format',
  image: 'metadata',
  pdf: 'metadata',
  vision: 'visual',
  'pixel-forensics': 'visual',
  'noise-residual': 'visual',
  'background-continuity': 'visual',
  'ocr-glyph-geometry': 'visual',
  date: 'dates',
  'siren-registry': 'registry',
  'tenant-rules': 'partnerRules',
} as const;

export type FraudSignalCategory =
  (typeof CATEGORY_BY_CODE_PREFIX)[keyof typeof CATEGORY_BY_CODE_PREFIX];

export const fraudSignalCategory = (code: string): FraudSignalCategory | undefined => {
  const prefix = code.split('.')[0];
  return Object.hasOwn(CATEGORY_BY_CODE_PREFIX, prefix)
    ? CATEGORY_BY_CODE_PREFIX[prefix as keyof typeof CATEGORY_BY_CODE_PREFIX]
    : undefined;
};
